import React, { useState } from 'react';
import { Box, Grid, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import Modal from 'new/components/Modal';
import Button from 'new/components/Button/Button';
import ListMultiSelect, { ListItem } from 'new/components/ListMultiSelect';
import { useGetLocations } from 'new/modules/ManageLocations/hooks';
import { useGetLocationGroups } from 'new/modules/ManageGroups/hooks';
import useActiveOffice from 'new/hooks/useActiveOffice';
import IconFontAwesome from 'new/components/IconFontAwesome';
import { TENANT } from 'new/tenants';

type Props = {
  open: boolean;
  onClose: () => void;
  isSuccess: boolean;
  isError: boolean;
  onSubmit: (items: ListItem[]) => void;
  reset: () => void;
  isLoading: boolean;
  isNewUser?: boolean;
  firstName?: string;
  lastName?: string;
  prevAssignedLocations?: { id: number; name?: string }[];
  prevAssignedGroups?: { id: number; name?: string }[];
};

const AssignModal = styled(Modal)(({ theme }) => ({
  fontFamily: TENANT.theme.modal.fontFamily,

  '.modalContent': {
    width: 800,
  },

  '.assignList': {
    maxHeight: 320,
    margin: theme.spacing(2, 0),
  },

  '.modalText': {
    fontStyle: 'italic',
    color: theme.palette.grey[600],
  },

  '.itemList': {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    height: 200,
    overflow: 'auto',
    marginBottom: theme.spacing(1),

    li: {
      fontWeight: 'bold',
    },
  },
}));

const AssignUserToLocationsGroupsModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  isNewUser,
  firstName,
  lastName,
  isSuccess,
  isError,
  reset,
  prevAssignedLocations,
  prevAssignedGroups,
}) => {
  const { user, orgId } = useActiveOffice();
  const [selectedItems, setSelectedItems] = useState<ListItem[]>([]);
  const { data: locations = [] } = useGetLocations(
    Number(orgId),
    user?.data.userId,
  );
  const { data: groups = [] } = useGetLocationGroups(
    orgId ? Number(orgId) : undefined,
  );
  const [isSecondStep, setIsSecondStep] = useState(false);

  const locationsList = locations
    .filter((location) => {
      if (prevAssignedLocations) {
        return prevAssignedLocations.every((item) => item.id !== location.id);
      }

      return true;
    })
    .sort((a, b) => a.locationName.localeCompare(b.locationName))
    .map((location) => ({
      key: location.id,
      value: location.locationName,
    }));

  const groupsList = groups
    .filter((group) => {
      if (prevAssignedGroups) {
        return prevAssignedGroups.every((item) => item.id !== group.id);
      }

      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((group) => ({
      key: group.id,
      value: `[G] ${group.name}`,
      type: 'group',
    }));

  const selectedGroups = selectedItems
    .filter((item) => item.type === 'group')
    .sort((a, b) => (a.value as string).localeCompare(b.value as string));

  const selectedLocations = selectedItems
    .filter((item) => item.type !== 'group')
    .sort((a, b) => (a.value as string).localeCompare(b.value as string));

  const numberSelectedGroups = selectedGroups.length;
  const numberSelectedLocations = selectedLocations.length;

  const renderSubmitLabel = () => {
    if (isSecondStep) {
      return "yes, i'm sure";
    }

    if (isNewUser) {
      return 'add user';
    } else {
      return 'assign user';
    }
  };

  const handleClose = () => {
    setSelectedItems([]);
    setIsSecondStep(false);
    reset();
    onClose();
  };

  return (
    <AssignModal
      open={open}
      onClose={handleClose}
      title={
        isSecondStep
          ? 'ARE YOU SURE?'
          : 'ASSIGN THIS USER TO GROUPS AND LOCATIONS'
      }
      isSuccess={isSuccess}
      isError={isError}
      successConfig={{
        mainMessage: 'SUCCESS!',
        secondaryMessage: (
          <Box width={400} color="#000">
            <Stack alignItems="center">
              <b>
                {firstName} {lastName}
              </b>
            </Stack>
            <Box mt={2} mb={3}>
              assigned to
            </Box>
            <Grid container>
              <Grid item xs={6}>
                <Stack alignItems="center" className="itemList">
                  {selectedGroups.map((item) => (
                    <Box key={item.key} fontWeight="bold">
                      {item.value}
                    </Box>
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack alignItems="center" className="itemList">
                  {selectedLocations.map((item) => (
                    <Box key={item.key} fontWeight="bold">
                      {item.value}
                    </Box>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        ),
        onClose: handleClose,
        buttonLabel: 'Close',
      }}
      errorConfig={{
        buttonLabel: 'Back',
        errorDescription: 'No changes were made.',
        onClose: reset,
      }}
    >
      {isSecondStep ? (
        <Stack alignItems="center">
          <IconFontAwesome
            name="faTriangleExclamationReg"
            color="warning"
            size="4x"
          />
          <Box>You're about to assign</Box>
          <Box component="b">
            {firstName} {lastName}
          </Box>
          <Box mt={3}>to the following: </Box>
          <Grid container>
            <Grid item xs={6} textAlign="center">
              Groups
              <Box component="ul" className="itemList">
                {selectedItems
                  .filter((item) => item.type === 'group')
                  .map((item) => (
                    <li key={item.key}>{item.value}</li>
                  ))}
              </Box>
            </Grid>
            <Grid item xs={6} textAlign="center">
              Locations
              <Box component="ul" className="itemList">
                {selectedItems
                  .filter((item) => item.type !== 'group')
                  .map((item) => (
                    <li key={item.key}>{item.value}</li>
                  ))}
              </Box>
            </Grid>
          </Grid>
        </Stack>
      ) : (
        <Stack alignItems="center" className="assignList">
          <Box className="modalText" mb={2}>
            Click groups and locations to select them
          </Box>
          <ListMultiSelect
            list={[...groupsList, ...locationsList]}
            checked={selectedItems}
            setChecked={setSelectedItems}
          />
          <Box className="modalText" mt={2}>
            {numberSelectedGroups} group
            {numberSelectedGroups > 1 ? 's' : ''} and {numberSelectedLocations}{' '}
            location
            {numberSelectedLocations > 1 ? 's' : ''} selected
          </Box>
        </Stack>
      )}
      <Stack direction="row" spacing={2} justifyContent="center">
        <Button
          color="secondary"
          variant="outlined"
          disabled={isLoading}
          onClick={() => {
            if (isSecondStep) {
              setIsSecondStep(false);
            } else {
              handleClose();
            }
          }}
        >
          go back
        </Button>
        <Button
          disabled={!numberSelectedGroups && !numberSelectedLocations}
          onClick={() => {
            if (isSecondStep) {
              onSubmit(selectedItems);
            } else {
              setIsSecondStep(true);
            }
          }}
          isLoading={isLoading}
        >
          {renderSubmitLabel()}
        </Button>
      </Stack>
    </AssignModal>
  );
};

export default AssignUserToLocationsGroupsModal;
